import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { sampleByEvent } from "posthog-js/lib/src/customizations";
import { environment, SentryUrl } from "./config";

let isSentryInitialized = false;

const initSentry = () => {
  if (isSentryInitialized || !SentryUrl || (environment !== "production" && environment !== "staging")) return;

  Sentry.init({
    dsn: SentryUrl,
    environment: environment,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  });

  isSentryInitialized = true;
};

const initPosthog = () => {
  if (environment !== "production") return;

  posthog.init("phc_oaQTfwuZHnUJDOiltWRtF1FIFOEF34vO74rsBSv0BgB", {
    api_host: "https://eu.i.posthog.com",
    defaults: "2025-05-24",
    capture_pageview: "history_change",
    autocapture: {
      dom_event_allowlist: ["click", "change"],
      css_selector_allowlist: ["button", "[data-track]"],
    },
    __add_tracing_headers: ["beta.api.querco.co", "api.querco.co"],
    before_send: sampleByEvent(["$web_vitals"], 0.1),
  });

  posthog.register({ source: "admin" });
};

const syncToSentry = (userId, indexName) => {
  if (!isSentryInitialized) return;

  Sentry.setUser({ id: userId });
  if (indexName) Sentry.setTag("indexName", indexName);
};

const identify = (id, indexName) => {
  const user = posthog.get_distinct_id();
  const groups = posthog.getGroups();

  if (user === id && groups?.indexName === indexName) return;

  posthog.identify(id);
  posthog.group("indexName", indexName);
  syncToSentry(id, indexName);
};

const toError = (error) => {
  if (error instanceof Error) return error;
  if (typeof error === "string") return new Error(error);
  const normalized = new Error(error?.message || error?.code || "Unknown error");
  if (error?.code) normalized.code = error.code;
  return normalized;
};

const capture = (error) => {
  if (error?.code === 401) return;

  console.error(error);

  if (!isSentryInitialized) return;

  const sessionId = posthog.get_session_id?.();
  const normalized = toError(error);

  Sentry.withScope((scope) => {
    if (sessionId) scope.setTag("posthog_session_id", sessionId);
    if (normalized.code) scope.setTag("error_code", normalized.code);
    if (normalized !== error) scope.setFingerprint([normalized.code || normalized.message]);
    Sentry.captureException(normalized);
  });
};

const track = (event, properties = {}) => {
  posthog.capture(event, properties);
};

export { posthog, initSentry, initPosthog, capture, track, identify };
